/**
 * Public Terms of Service page. Linked from /signup, the global
 * footer, and any place we ask the user to consent.
 *
 * Drafted from common SaaS terms; **Brad — read this like a contract
 * before linking it on a public landing page**. Items he needs to
 * verify or replace:
 *
 *   - Governing-law jurisdiction (currently South Carolina)
 *   - Mailing address for legal notice
 *   - Late-fee percentage + grace period
 *   - Data retention period after account deletion
 *   - Refund policy specifics
 *
 * Update :data:`TERMS_VERSION` when the substantive terms change so
 * /signup re-prompts for acceptance.
 *
 * Legal entity: Bramar Strategic Services LLC. PencilOut is the
 * customer-facing brand operated by Bramar; Veridon Consulting is
 * a separate brand under the same LLC. Both share these Terms.
 */

import { Link } from "react-router-dom";

export const TERMS_VERSION = "2026-04-27b";

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" version={TERMS_VERSION}>
      <Section title="1. Agreement">
        <P>
          These Terms of Service ("Terms") form a binding agreement between you ("you", "your", or
          "Customer") and <strong>Bramar Strategic Services LLC</strong>, doing business as
          PencilOut ("PencilOut", "we", "us", or "our"). By creating an account, accessing the
          service, or clicking "I agree," you accept these Terms. If you don't agree, don't use
          PencilOut.
        </P>
      </Section>

      <Section title="2. The Service">
        <P>
          PencilOut is a software service that converts contractor walkthrough videos into draft
          quotes using AI processing, lets you edit and send those quotes to your clients, and
          tracks the full quote lifecycle (sent, viewed, accepted, declined). Specific features may
          be added, changed, or removed; substantive changes are announced to active customers.
        </P>
      </Section>

      <Section title="3. Account &amp; Eligibility">
        <P>
          You must be at least 18 years old and authorized to bind your organization. You're
          responsible for all activity under your account, including activity by users you invite.
          Keep your credentials secret; we'll never ask for your password by email.
        </P>
        <P>
          One person, one email. Don't share logins. If your contact email ceases to be valid (job
          change, etc.), update it before access is locked out — magic-link sign-in depends on email
          reachability.
        </P>
      </Section>

      <Section title="4. Acceptable Use">
        <P>You agree not to:</P>
        <Ul>
          <li>
            Upload content that is unlawful, infringes another party's rights, or contains malware.
          </li>
          <li>
            Use PencilOut to defraud your clients, including misrepresenting quote totals or hiding
            material costs.
          </li>
          <li>
            Reverse-engineer, scrape, or systematically extract data from PencilOut except your own
            organization's data via documented export endpoints.
          </li>
          <li>Resell or sublicense the service to a third party without our written consent.</li>
          <li>
            Use PencilOut to send unsolicited bulk email or otherwise abuse the messaging features.
          </li>
        </Ul>
        <P>
          We may suspend or terminate accounts that violate these rules. Egregious cases (CSAM,
          malware distribution, etc.) result in immediate termination + reporting to the relevant
          authorities.
        </P>
      </Section>

      <Section title="5. Your Data &amp; Content">
        <P>
          You retain all rights to the data you upload — walkthrough videos, job details, client
          information, line items. We store and process this data solely to provide the service and
          our reasonable internal operations (security monitoring, fraud prevention, aggregate
          analytics that don't identify your clients).
        </P>
        <P>
          Walkthrough videos are processed by third-party AI providers to produce transcripts and
          scope summaries. Quote line items are generated based on your walkthrough + your pricing
          rules. You can read more about our subprocessors in the{" "}
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          .
        </P>
        <P>
          We don't use your customer data to train any AI model. Period. The AI providers we use
          contractually agree not to either.
        </P>
      </Section>

      <Section title="6. AI-Generated Content">
        <P>
          PencilOut's AI generates drafts, not finished products. You're responsible for reviewing
          every quote before sending it to a client. AI hallucinations, mis-priced line items,
          missing scope, or wrong quantities are your responsibility to catch — that's why quotes
          start as <code>draft</code> and require explicit contractor review before sending. We
          provide tools to help you review (confidence scores, source quotes, hidden-line-item
          review) but the final professional judgment is yours.
        </P>
        <P>Do not send a quote to a client without reading it.</P>
      </Section>

      <Section title="7. Fees &amp; Billing">
        <P>
          Pricing is set at signup and confirmed via your Settings page. Two billable components:
        </P>
        <Ul>
          <li>
            <strong>AI usage</strong> (pass-through, with our markup) — invoiced monthly for the
            prior month's usage. Rates and markup are visible in your AI Usage report.
          </li>
          <li>
            <strong>Platform subscription</strong> (when applicable) — monthly recurring;
            auto-renews unless cancelled.
          </li>
        </Ul>
        <P>
          Invoices are payable within 14 days of issue. Late payments may incur a 1.5% monthly fee
          or the legal maximum, whichever is lower. Disputed charges must be raised in writing
          within 30 days of the invoice date; charges not disputed in writing are deemed accepted.
        </P>
        <P>
          We use Stripe to process payments. By providing payment information you authorize Stripe
          to charge your designated payment method according to these Terms.
        </P>
      </Section>

      <Section title="8. Cancellation &amp; Refunds">
        <P>
          You may cancel at any time from your Settings or by emailing{" "}
          <a href="mailto:support@pencilout.app" className="underline">
            support@pencilout.app
          </a>
          . Cancellation takes effect at the end of the current billing cycle; AI usage incurred up
          to that point is billed normally. Pre-paid platform subscription fees are not refundable
          except as required by law or as noted on a specific promotion.
        </P>
      </Section>

      <Section title="9. Service Availability &amp; Changes">
        <P>
          We aim for high availability but don't guarantee uptime targets for the pilot phase.
          Scheduled maintenance is announced when practical; unscheduled outages happen — we'll
          communicate via email + the status indicator in the admin UI when we have one.
        </P>
        <P>
          We may change features, add capacity limits, or sunset the service. Substantive changes
          are announced with reasonable advance notice (30 days minimum for paid customers). Trivial
          changes (bug fixes, UI tweaks) require no notice.
        </P>
      </Section>

      <Section title="10. Termination">
        <P>
          You may terminate at any time per Section 8. We may terminate or suspend your access
          immediately for material breach of these Terms (Section 4 violations, payment failure
          beyond the cure period, etc.) or with 30 days' notice for any reason.
        </P>
        <P>
          On termination, you may export your organization's data via documented endpoints for 30
          days. After that, we may permanently delete your data, subject to our retention
          obligations under law.
        </P>
      </Section>

      <Section title="11. Intellectual Property">
        <P>
          PencilOut owns the software, brand, designs, and documentation ("PencilOut IP"). Your
          subscription gives you a non-exclusive, non-transferable right to use the service. You
          don't acquire any PencilOut IP.
        </P>
        <P>
          Conversely, you retain ownership of your data and quotes generated from your input. We
          claim no ownership over your quotes, line items, or client list.
        </P>
      </Section>

      <Section title="12. Warranty Disclaimer">
        <P>
          The service is provided "as is" without warranties of any kind, express or implied. We
          disclaim all implied warranties including merchantability, fitness for a particular
          purpose, and non- infringement. AI-generated content is best-effort and not guaranteed
          accurate.
        </P>
      </Section>

      <Section title="13. Limitation of Liability">
        <P>
          To the maximum extent permitted by law, neither party will be liable for indirect,
          incidental, special, consequential, or punitive damages, or for lost profits or revenues.
          Our total aggregate liability under these Terms is limited to the amount you paid us in
          the 12 months preceding the claim. Nothing here limits liability for fraud, willful
          misconduct, or anything else that can't be limited under applicable law.
        </P>
      </Section>

      <Section title="14. Indemnification">
        <P>
          You'll defend and indemnify PencilOut against third-party claims arising from (a) your use
          of the service in violation of these Terms, (b) your data or content, or (c) the actual or
          alleged infringement of any rights of a third party (including your clients or employees)
          by your acts or omissions. We'll defend and indemnify you against third-party
          IP-infringement claims targeting the PencilOut IP itself.
        </P>
      </Section>

      <Section title="15. Governing Law">
        <P>
          These Terms are governed by the laws of the State of South Carolina, USA, without regard
          to conflict-of-law rules. The state and federal courts in York County, South Carolina have
          exclusive jurisdiction.
        </P>
      </Section>

      <Section title="16. Changes to These Terms">
        <P>
          We may update these Terms; the version date appears at the top. Material changes will be
          communicated by email + an in-app banner before they take effect. Continued use of the
          service after the effective date constitutes acceptance.
        </P>
      </Section>

      <Section title="17. Contact">
        <P>
          Questions or legal notices:{" "}
          <a href="mailto:support@pencilout.app" className="underline">
            support@pencilout.app
          </a>
          . Mailing address available on request.
        </P>
      </Section>
    </LegalShell>
  );
}

// ---------------------------------------------------------------
// Reusable shell + components used by both legal pages.
// ---------------------------------------------------------------

export function LegalShell({
  title,
  version,
  children,
}: {
  title: string;
  version: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-paper text-graphite">
      <header className="border-b border-rule bg-paper-warm">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to="/" className="brand-wordmark text-2xl">
            PencilOut
          </Link>
          <Link to="/login" className="font-mono text-xs text-graphite-soft underline">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="font-mono text-3xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 font-mono text-xs text-graphite-soft">Version {version}</p>

        <div className="mt-8 space-y-6">{children}</div>

        <footer className="mt-16 border-t border-rule pt-6 text-center font-mono text-xs text-graphite-soft">
          <Link to="/terms" className="underline">
            Terms
          </Link>
          {" · "}
          <Link to="/privacy" className="underline">
            Privacy
          </Link>
          {" · "}
          <a href="mailto:support@pencilout.app" className="underline">
            support@pencilout.app
          </a>
        </footer>
      </main>
    </div>
  );
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-mono text-base font-semibold uppercase tracking-wider text-graphite">
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-graphite">{children}</div>
    </section>
  );
}

export function P({ children }: { children: React.ReactNode }) {
  return <p>{children}</p>;
}

export function Ul({ children }: { children: React.ReactNode }) {
  return <ul className="ml-5 list-disc space-y-1.5">{children}</ul>;
}
